import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { createEditSolutionFormValues } from "../../../../edit/form/defaultValues";
import type { EditSolutionFormValues } from "../../../../edit/form/types";
import { editSolutionFormSchema } from "../../../../edit/form/schema";
import EarthlySectionEditInputs from "./EarthlySectionEditInputs";
import type {
  EarthlySectionEditModalProps,
  EarthlySectionId,
} from "../../types";

const sectionTitles: Record<EarthlySectionId, string> = {
  "publishing-summary": "Publishing summary",
  "project-introduction": "Project introduction",
  "key-information": "Key information",
  scores: "Scores",
  "commercial-information": "Commercial information",
  "project-information": "Project information",
  benefits: "Benefits",
  "map-information": "Map information",
  "project-narratives": "Project narratives",
};

const submissionErrorMessage = "Unable to save changes. Please try again.";

function EarthlySectionEditModal({
  section,
  details,
  loading,
  error,
  onClose,
  onSubmit,
}: EarthlySectionEditModalProps) {
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const closeModal = () => {
    setSubmissionError(null);
    onClose();
  };

  if (!section) return null;

  return (
    <Dialog
      open
      onClose={loading ? undefined : closeModal}
      fullWidth
      maxWidth="md"
      data-testid="earthly-section-edit-modal"
    >
      <Formik<EditSolutionFormValues>
        key={`${section}-${details.id}`}
        initialValues={createEditSolutionFormValues(details)}
        validationSchema={editSolutionFormSchema}
        validateOnMount
        onSubmit={async (values, helpers) => {
          setSubmissionError(null);

          try {
            const succeeded = await onSubmit(values);
            if (succeeded) closeModal();
            else setSubmissionError(submissionErrorMessage);
          } catch {
            setSubmissionError(submissionErrorMessage);
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isValid, isSubmitting, submitCount }) => {
          const submitting = loading || isSubmitting;
          const displayedSubmissionError =
            submitCount > 0 ? (error ?? submissionError) : submissionError;

          return (
            <Box component="form" onSubmit={handleSubmit}>
              <DialogTitle>Edit {sectionTitles[section]}</DialogTitle>
              <DialogContent dividers>
                {displayedSubmissionError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {displayedSubmissionError}
                  </Alert>
                )}
                {!isValid && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Please correct the highlighted fields before saving.
                  </Alert>
                )}
                <EarthlySectionEditInputs section={section} />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={closeModal}
                  disabled={submitting}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={!isValid}
                  variant="contained"
                >
                  Save
                </Button>
              </DialogActions>
            </Box>
          );
        }}
      </Formik>
    </Dialog>
  );
}

export default EarthlySectionEditModal;
